import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useTranslation } from "@/i18n/translations";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: code & new password
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    reset_code: "",
    new_password: "",
    confirm_password: ""
  });

  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email: formData.email
      });
      toast.success(t('forgot.codeSent'));
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('forgot.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (formData.new_password !== formData.confirm_password) {
      toast.error(t('forgot.passwordMismatch'));
      return;
    }

    if (formData.new_password.length < 6) {
      toast.error(t('forgot.passwordTooShort'));
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: formData.email,
        reset_code: formData.reset_code,
        new_password: formData.new_password
      });
      toast.success(t('forgot.success'));
      navigate('/auth');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('forgot.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-2xl p-8 border-4 border-black">
          <button
            onClick={() => navigate('/auth')}
            className="flex items-center gap-2 text-gray-600 hover:text-black mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">{t('forgot.backToLogin')}</span>
          </button>

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-black mb-2">
              {t('forgot.title')}
            </h1>
            <p className="text-gray-600 text-sm">
              {step === 1 ? t('forgot.subtitle') : t('forgot.enterCode')}
            </p>
          </div>

          {step === 1 ? (
            <form onSubmit={handleSendCode} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-black font-medium">
                  {t('auth.email')}
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="mt-1 border-2 border-black focus:ring-2 focus:ring-black text-black"
                  placeholder="your@email.com"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-black text-white hover:bg-gray-800 font-medium py-6 text-lg border-2 border-black"
              >
                {loading ? t('auth.loading') : t('forgot.sendCode')}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <Label htmlFor="reset_code" className="text-black font-medium">
                  {t('forgot.resetCode')}
                </Label>
                <Input
                  id="reset_code"
                  type="text"
                  value={formData.reset_code}
                  onChange={(e) => setFormData({ ...formData, reset_code: e.target.value })}
                  required
                  maxLength={6}
                  className="mt-1 border-2 border-black focus:ring-2 focus:ring-black text-center text-2xl tracking-widest text-black"
                  placeholder="123456"
                />
              </div>

              <div>
                <Label htmlFor="new_password" className="text-black font-medium">
                  {t('forgot.newPassword')}
                </Label>
                <Input
                  id="new_password"
                  type="password"
                  value={formData.new_password}
                  onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                  required
                  className="mt-1 border-2 border-black focus:ring-2 focus:ring-black text-black"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <Label htmlFor="confirm_password" className="text-black font-medium">
                  {t('forgot.confirmPassword')}
                </Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={formData.confirm_password}
                  onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                  required
                  className="mt-1 border-2 border-black focus:ring-2 focus:ring-black text-black"
                  placeholder="••••••••"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-black text-white hover:bg-gray-800 font-medium py-6 text-lg border-2 border-black"
              >
                {loading ? t('auth.loading') : t('forgot.resetPassword')}
              </Button>

              <button
                type="button"
                onClick={() => setStep(1)}
                className="w-full text-sm text-gray-600 hover:text-black underline mt-4"
              >
                {t('forgot.resendCode')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
